import type { Post } from '@/@types/post'
import MoreMenuButton from '@/components//MoreMenuButton'
import { BlurTextMode, FeaturableBlurTextSwitch } from '@/components/BlurTextSwitch'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { POST_TYPE_TO_ROUTE_MAPPING } from '@/utils/route'
import { css } from '@emotion/react'
import { BubbleIcon } from '@ruguoapp/jike-fe-icons'
import { isEqual } from 'lodash-es'
import { type HTMLAttributes, memo } from 'react'
import ActionButton from '../../ActionButton'
import DynamicLikeIcon from '../DynamicLikeIcon'
import { usePostActionsContext } from '../PostActionsContext'
import RepostMenuButton from './RepostMenuButton'
import useMoreActionsMenuData from './useMoreActionsMenuData'
import useMoreActionsMenuSelectHandler from './useMoreActionsMenuSelectHandler'
import { usePostLikeToggle } from './usePostActionHandlers'

export interface PostActionsProps extends HTMLAttributes<HTMLDivElement> {
	data: Post
}

export default memo(function PostActions({ data, ...props }: PostActionsProps) {
	const { id, user, likeCount = 0, commentCount = 0 } = data
	const { toggleLike } = usePostLikeToggle(data.id)
	const navigate = useUniversalNavigate()

	const { onComment } = usePostActionsContext()

	const moreMenuData = useMoreActionsMenuData(data)
	const handleMoreActionMenuItemClick = useMoreActionsMenuSelectHandler(data)

	return (
		<div
			{...props}
			css={css`
				display: flex;
				gap: 26px;
				align-items: center;
				color: var(--tint-secondary);

				/* Body 4 */
				font-size: 14px;
				line-height: 20px; /* 142.857% */

				.action {
					width: 70px;
					user-select: none;
					cursor: pointer;
					font-variant-numeric: tabular-nums;
				}
			`}
		>
			<ActionButton
				onClick={() => toggleLike()}
				css={css`
					position: relative;
					padding-left: 32px;
				`}
				icon={
					<DynamicLikeIcon
						data={data}
						css={css`
							width: 20px;
							height: 20px;
							transition: color 0.2s;
							position: absolute;
							left: 0;
							top: 50%;
							z-index: 1;
							transform: translateY(-50%);
							${data?.liked &&
							css`
								color: var(--tint-error);
							`}
						`}
					/>
				}
			>
				<FeaturableBlurTextSwitch data={likeCount.toString()} mode={BlurTextMode.SMALL} />
			</ActionButton>

			<ActionButton
				onClick={(e) => {
					if (onComment) {
						onComment(data)
						return
					}
					navigate(
						{
							to: '/u/$username/$type/$id',
							params: {
								username: user?.username,
								id,
								type: POST_TYPE_TO_ROUTE_MAPPING[data.type],
							},
							hash: 'comments',
						},
						e
					)
				}}
				icon={
					<BubbleIcon
						width="20px"
						height="20px"
						css={css`
							transition: color 0.2s;
						`}
					/>
				}
			>
				<FeaturableBlurTextSwitch data={commentCount.toString()} mode={BlurTextMode.SMALL} />
			</ActionButton>

			<RepostMenuButton data={data} />

			<section
				css={css`
					margin-left: auto;
				`}
			>
				<MoreMenuButton data={moreMenuData} onSelect={handleMoreActionMenuItemClick} />
			</section>
		</div>
	)
}, isEqual)
